// ============================================================
//  LIVE TRIP BARS — ADMIN DASHBOARD
//  Path: src/components/TripBars.tsx
//
//  The same "on my way" bars the customer and the tasker see, for every
//  trip under way right now (GET /api/admin/trips). Read-only. The server
//  sends distances and progress only — never coordinates — so this panel
//  cannot show a map or a location trail, by design.
//
//  Used on the main dashboard (all trips) and on a tasker's page
//  (expertId = that tasker). Refreshes every 20 s while the tab is visible.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react'
import { listActiveTrips } from '../services/admin.service'

type TripStage = {
  key: 'toJob' | 'toPickup' | 'toDropoff' | 'toStore' | string
  toName?: string | null
  state: 'done' | 'active' | 'upcoming'
  remainingKm: number | null
  totalKm: number | null
  progress: number | null
  atPlace?: boolean
}

export type ActiveTrip = {
  taskId: string
  title?: string | null
  category?: string | null
  tasker: { expertId: string | null; name: string | null; company: string | null }
  customerName: string | null
  traveller: 'tasker' | 'customer'
  deliveryStatus?: string
  twoLocation?: boolean
  live: boolean
  positionAgeSeconds: number | null
  stages: TripStage[]
}

const stageLabel = (s: TripStage, traveller: string): string => {
  switch (s.key) {
    case 'toJob':     return 'Tasker → job address'
    case 'toPickup':  return `Tasker → pickup${s.toName && s.toName !== 'Pickup' ? ` (${s.toName})` : ''}`
    case 'toDropoff': return 'Pickup → drop-off'
    case 'toStore':   return 'Customer → tasker’s location'
    default:          return traveller === 'customer' ? 'Customer → destination' : 'Tasker → destination'
  }
}

const kmText = (km: number | null | undefined): string =>
  km == null ? '—' : km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`

const ageText = (sec: number | null): string => {
  if (sec == null) return 'no position yet'
  if (sec < 90) return 'live'
  const m = Math.round(sec / 60)
  return m < 60 ? `last seen ${m} min ago` : 'last seen over an hour ago'
}

const StageBar: React.FC<{ stage: TripStage; traveller: string; live: boolean }> = ({ stage, traveller, live }) => {
  const pct = stage.state === 'done' ? 100
    : stage.state === 'upcoming' ? 0
    : Math.round(Math.min(Math.max(stage.progress ?? 0, 0), 1) * 100)
  const dim = stage.state === 'upcoming'
  return (
    <div className={dim ? 'opacity-50' : ''}>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-gray-300">{stageLabel(stage, traveller)}</span>
        <span className="text-gray-400">
          {stage.state === 'done' ? 'done'
            : stage.atPlace ? 'at pickup'
            : `${kmText(stage.remainingKm)} left`}
        </span>
      </div>
      <div className="relative h-1.5 rounded-full bg-surface-tertiary">
        <div className={`absolute left-0 top-0 h-1.5 rounded-full ${live || stage.state !== 'active' ? 'bg-green-500' : 'bg-gray-500'}`}
             style={{ width: `${pct}%` }} />
        {stage.state === 'active' && (
          <span className="absolute -top-2.5 text-base leading-none"
                style={{ left: `calc(${pct}% - 8px)`, transform: 'scaleX(-1)' }} aria-hidden>🚗</span>
        )}
        <span className="absolute -right-1 -top-1.5 text-xs leading-none" aria-hidden>🏁</span>
      </div>
    </div>
  )
}

export const TripCard: React.FC<{ trip: ActiveTrip; showTasker?: boolean }> = ({ trip, showTasker = true }) => (
  <div className="bg-surface-tertiary/40 border border-border-light rounded-xl p-4">
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="min-w-0">
        {showTasker && (
          <p className="text-sm font-medium text-white truncate">
            {trip.tasker.name || 'Tasker'}{trip.tasker.company ? ` · ${trip.tasker.company}` : ''}
          </p>
        )}
        <p className="text-xs text-gray-400 truncate">
          {[trip.category, trip.title].filter(Boolean).join(' · ') || 'Task'}
          {trip.customerName ? ` — for ${trip.customerName}` : ''}
        </p>
      </div>
      <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] ${
        trip.live ? 'bg-green-600/20 text-green-400' : 'bg-yellow-600/20 text-yellow-400'
      }`}>
        {trip.traveller === 'customer' ? 'Customer travelling · ' : ''}{ageText(trip.positionAgeSeconds)}
      </span>
    </div>
    <div className="space-y-4 pt-2">
      {trip.stages.map((s) => <StageBar key={s.key} stage={s} traveller={trip.traveller} live={trip.live} />)}
    </div>
  </div>
)

const POLL_MS = 20_000

const TripBars: React.FC<{ expertId?: string; title?: string; emptyText?: string }> = ({
  expertId, title = 'Live trips', emptyText = 'Nobody is on the way right now.',
}) => {
  const [trips, setTrips]     = useState<ActiveTrip[] | null>(null)
  const [failed, setFailed]   = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await listActiveTrips(expertId ? { expertId } : undefined)
      setTrips(Array.isArray(res.data?.data) ? res.data.data : [])
      setFailed(false)
    } catch {
      setFailed(true)
    }
  }, [expertId])

  useEffect(() => {
    load()
    const t = setInterval(() => { if (!document.hidden) load() }, POLL_MS)
    return () => clearInterval(t)
  }, [load])

  return (
    <div className="bg-surface-card border border-border-light rounded-2xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        {trips && trips.length > 0 && <span className="text-xs text-gray-400">{trips.length} on the way</span>}
      </div>
      {failed && trips === null && <p className="text-sm text-red-400">Could not load trips.</p>}
      {trips === null && !failed && <p className="text-sm text-gray-500">Loading…</p>}
      {trips && trips.length === 0 && <p className="text-sm text-gray-500">{emptyText}</p>}
      {trips && trips.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          {trips.map((t) => <TripCard key={t.taskId} trip={t} showTasker={!expertId} />)}
        </div>
      )}
      {failed && trips !== null && <p className="text-xs text-yellow-500 mt-3">Showing the last update — refresh failed.</p>}
    </div>
  )
}

export default TripBars